import {
  useSendApiErrorNotification,
  useSendNotification,
} from "@app/hooks/useNotification";
import { clientFetch } from "@app/lib/egress/client";
import type { GetWorkspaceUsageStatusResponseBody } from "@app/lib/metronome/user_block";
import type { GetUserApprovalsResponseBody } from "@app/lib/resources/user_resource";
import { nonRedirectingFetcher } from "@app/lib/swr/fetcher";
import {
  emptyArray,
  getErrorFromResponse,
  useFetcher,
  useSWRWithDefaults,
} from "@app/lib/swr/swr";
import type { EmailProviderType } from "@app/lib/utils/email_provider_detection";
import type { GetPendingInvitationsResponseBody } from "@app/types/api/invitation";
import type { GetUserMemoryResponseBody } from "@app/types/api/me/memory";
import type { GetSlackNotificationResponseBody } from "@app/types/api/me/slack_notifications";
import type {
  GetUserMetadataResponseBody,
  GetUserResponseBody,
} from "@app/types/api/user";
import type { FavoritePlatform } from "@app/types/favorite_platforms";
import type { JobType } from "@app/types/job_type";
import type { SupportedLocale } from "@app/types/locale";
import type { LightWorkspaceType } from "@app/types/user";
import { useLingui } from "@lingui/react/macro";
import { useCallback, useMemo, useState } from "react";
import type { Fetcher, SWRConfiguration } from "swr";

export function useUser(
  swrOptions?: SWRConfiguration & {
    disabled?: boolean;
    // Defaults to true. Set to false on pages where a 401 should leave the
    // page in place rather than bounce the visitor through the login flow.
    redirectOnUnauthenticated?: boolean;
  }
) {
  const { fetcher } = useFetcher();
  const skipRedirect = swrOptions?.redirectOnUnauthenticated === false;
  const userFetcher: Fetcher<GetUserResponseBody> = skipRedirect
    ? nonRedirectingFetcher
    : fetcher;
  const { data, error, mutate } = useSWRWithDefaults("/api/user", userFetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateIfStale: false,
    // 401 here is the expected outcome for anonymous visitors -> don't retry
    // (default would log it to datadog 16 times).
    ...(skipRedirect ? { shouldRetryOnError: false } : {}),
    ...swrOptions,
  });

  return {
    user: data ? data.user : null,
    isUserLoading: !error && !data,
    isUserError: error,
    mutateUser: mutate,
  };
}

export function useUserMetadata(
  key: string,
  swrOptions?: SWRConfiguration & {
    disabled?: boolean;
    workspaceId?: string;
  }
) {
  const { fetcher } = useFetcher();
  const userMetadataFetcher: Fetcher<GetUserMetadataResponseBody> = fetcher;

  let url = `/api/user/metadata/${encodeURIComponent(key)}`;
  if (swrOptions?.workspaceId) {
    url += `?workspaceId=${encodeURIComponent(swrOptions.workspaceId)}`;
  }

  const { data, error, mutate } = useSWRWithDefaults(url, userMetadataFetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateIfStale: false,
    ...swrOptions,
  });

  return {
    metadata: data ? data.metadata : null,
    isMetadataLoading: !error && !data,
    isMetadataError: error,
    mutateMetadata: mutate,
  };
}

/** @cc [owner:philipperolet,label:product] email-footer-dismissal
 * Dismissal persists for the user across all agents and workspaces. Hide the footer until the
 * preference has loaded, and only mark it dismissed after the save succeeds.
 */
export function useEmailAgentFooter({ disabled }: { disabled: boolean }) {
  const key = "emailAgentFooterDismissed";
  const { metadata, isMetadataLoading, isMetadataError, mutateMetadata } =
    useUserMetadata(key, { disabled });
  const [isDismissing, setIsDismissing] = useState(false);
  const { t } = useLingui();
  const sendNotification = useSendNotification();

  const dismissFooter = async () => {
    setIsDismissing(true);
    try {
      const response = await clientFetch(`/api/user/metadata/${key}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ value: "true" }),
      }).catch(() => null);

      if (!response?.ok) {
        sendNotification({
          type: "error",
          title: t`Could not dismiss the email tip`,
          description: t`Please try again.`,
        });
        return;
      }

      await mutateMetadata(
        { metadata: { key, value: "true" } },
        { revalidate: false }
      );
    } finally {
      setIsDismissing(false);
    }
  };

  return {
    showEmailFooter:
      !disabled &&
      !isMetadataLoading &&
      !isMetadataError &&
      metadata?.value !== "true",
    dismissFooter,
    isDismissing,
  };
}

export function useUserApprovals(owner: LightWorkspaceType) {
  const { fetcher } = useFetcher();
  const userApprovalsFetcher: Fetcher<GetUserApprovalsResponseBody> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    `/api/w/${owner.sId}/me/approvals`,
    userApprovalsFetcher
  );

  return {
    approvals: data ? data.approvals : [],
    isApprovalsLoading: !error && !data,
    isApprovalsError: error,
    mutateApprovals: mutate,
  };
}

export function useCompleteUserOnboarding() {
  const { t } = useLingui();
  const sendApiErrorNotification = useSendApiErrorNotification();

  // Returns whether the onboarding was completed. Omitting `locale` leaves the user's locale
  // unchanged.
  const completeUserOnboarding = async ({
    locale,
  }: {
    locale?: SupportedLocale;
  }) => {
    const res = await clientFetch("/api/user/onboarding/complete", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ locale }),
    });

    if (!res.ok) {
      const errorData = await getErrorFromResponse(res);
      sendApiErrorNotification({
        title: t`Error completing onboarding`,
        error: errorData,
      });
      return false;
    }

    return true;
  };

  return { completeUserOnboarding };
}

export function useDeleteToolApproval() {
  const deleteToolApproval = async (
    owner: LightWorkspaceType,
    mcpServerId: string
  ) => {
    return clientFetch(
      `/api/w/${owner.sId}/me/approvals?mcpServerId=${encodeURIComponent(mcpServerId)}`,
      { method: "DELETE" }
    );
  };

  return { deleteToolApproval };
}

export function usePatchUser() {
  const { mutateUser } = useUser();
  const { t } = useLingui();
  const sendApiErrorNotification = useSendApiErrorNotification();
  const sendNotification = useSendNotification();

  const patchUser = async ({
    firstName,
    lastName,
    notifySuccess,
    jobType,
    imageUrl,
    favoritePlatforms,
    emailProvider,
    workspaceId,
    pronouns,
  }: {
    firstName: string;
    lastName: string;
    notifySuccess: boolean;
    jobType?: JobType;
    imageUrl?: string | null;
    favoritePlatforms?: FavoritePlatform[];
    emailProvider?: EmailProviderType;
    workspaceId?: string;
    pronouns?: string | null;
  }) => {
    const res = await clientFetch("/api/user", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        firstName,
        lastName,
        jobType,
        imageUrl,
        favoritePlatforms,
        emailProvider,
        workspaceId,
        pronouns,
      }),
    });

    if (res.ok) {
      if (notifySuccess) {
        sendNotification({
          type: "success",
          title: t`Profile updated`,
          description: t`Successfully updated your profile.`,
        });
      }

      await mutateUser();

      return res.json();
    } else {
      const errorData = await getErrorFromResponse(res);
      sendApiErrorNotification({
        title: t`Error updating profile`,
        error: errorData,
      });

      return null;
    }
  };

  return { patchUser };
}

export function usePendingInvitations({
  workspaceId,
  disabled,
}: {
  workspaceId: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const pendingInvitationsFetcher: Fetcher<GetPendingInvitationsResponseBody> =
    fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    `/api/w/${workspaceId}/me/pending-invitations`,
    pendingInvitationsFetcher,
    { disabled }
  );

  return {
    pendingInvitations: data?.pendingInvitations ?? emptyArray(),
    isPendingInvitationsLoading: !error && !data && !disabled,
    isPendingInvitationsError: error,
    mutatePendingInvitations: mutate,
  };
}

export function useUserMemory({
  owner,
  disabled,
}: {
  owner: LightWorkspaceType;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const { t } = useLingui();
  const sendApiErrorNotification = useSendApiErrorNotification();
  const sendNotification = useSendNotification();
  const memoryFetcher: Fetcher<GetUserMemoryResponseBody> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    `/api/w/${owner.sId}/me/memory`,
    memoryFetcher,
    { disabled }
  );

  const setMemory = useCallback(
    async (update: {
      content?: string;
      enabled?: boolean;
    }): Promise<boolean> => {
      const res = await clientFetch(`/api/w/${owner.sId}/me/memory`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(update),
      });

      if (res.ok) {
        sendNotification({ type: "success", title: t`Memory saved` });
        await mutate();
        return true;
      }

      const errorData = await getErrorFromResponse(res);
      sendApiErrorNotification({
        title: t`Error saving memory`,
        error: errorData,
      });
      return false;
    },
    [owner.sId, sendNotification, mutate, sendApiErrorNotification, t]
  );

  return {
    content: data?.content ?? "",
    isMemoryEnabled: data?.enabled ?? false,
    isMemoryLoading: !error && !data && !disabled,
    isMemoryError: error,
    mutateMemory: mutate,
    setMemory,
  };
}

export function useSlackNotifications(
  workspaceId: string,
  options?: {
    disabled?: boolean;
  }
) {
  const { fetcher } = useFetcher();
  const slackNotificationsFetcher: Fetcher<GetSlackNotificationResponseBody> =
    fetcher;

  const { data, isLoading } = useSWRWithDefaults(
    `/api/w/${workspaceId}/me/slack-notifications`,
    slackNotificationsFetcher,
    { disabled: options?.disabled }
  );

  const isSlackSetupLoading = isLoading && !options?.disabled;

  const canConfigureSlack = useMemo(() => {
    return data?.canConfigure === true;
  }, [data]);

  return {
    isSlackSetupLoading,
    canConfigureSlack,
  };
}

export function useWorkspaceUsageStatus({
  owner,
  disabled,
}: {
  owner: LightWorkspaceType;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const usageStatusFetcher: Fetcher<GetWorkspaceUsageStatusResponseBody> =
    fetcher;
  const { data, error } = useSWRWithDefaults(
    `/api/w/${owner.sId}/usage-status`,
    usageStatusFetcher,
    { disabled }
  );

  return {
    userNearCreditLimit: data?.userNearCreditLimit ?? false,
    poolCreditState: data?.poolCreditState ?? "active",
    programmaticCreditStatus: data?.programmaticCreditStatus ?? "active",
    programmaticWarningReached: data?.programmaticWarningReached ?? false,
    balanceThresholdReached: data?.balanceThresholdReached ?? false,
    userBlockedReason: data?.userBlockedReason ?? null,
    canRequestUpgrade: data?.canRequestUpgrade ?? false,
    hasPendingUpgradeRequest: data?.hasPendingUpgradeRequest ?? false,
    willAutoUpgrade: data?.willAutoUpgrade ?? false,
    requireReason: data?.requireReason ?? false,
    isUsageStatusLoading: !error && !data && !disabled,
  };
}
